import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FileSpreadsheet, FileText, Lock, User } from 'lucide-react';

import Button from '../../../components/ui/Button';
import DataTable from '../../../components/ui/DataTable';
import DateTimePicker from '../../../components/ui/DateTimePicker';
import GenericSelect from '../../../components/ui/GenericSelect';
import PmAnalyticsFilterBar from '../../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod } from '../../../components/performance/pmAnalyticsFilters';
import useLayoutBucket from '../../../hooks/useLayoutBucket';
import { formatDateTime } from '../../../utils/Utils';
import { fetchPeriods } from '../../performance-operations/config';
import MetricStrip from './MetricStrip';
import {
  Badge, Description, Field, Fixed, Meta, Notes, Row, StateBox, TableArea,
} from '../styles';
import {
  DATE_FILTERS, FAMILIES, HIDDEN_FILTERS, SURVEY_DIMENSIONS, buildColumns, buildLabels, buildQuery, downloadReport, extraOptions, fetchMyRuns,
  fetchPreview, fetchTemplates, metricLabel, templateOptions,
} from '../config';

// PM analitik P7b — E16 rapor kataloğu + süzgeç + ön izleme + indirme. Akış: katalog → süzgeç → ön izleme (aynı süzgeçle sayfalı
// satırlar) → Excel (detay) / PDF (sunucuda yönetim özeti). Şablon ve ortak süzgeçler URL'dedir (geri dönüşte korunur).
const TPL_PARAM = 'tpl';

export default function ReportWorkspace({ isHr }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const location = useLocation();
  const history = useHistory();
  const bucket = useLayoutBucket();
  const narrow = bucket === 'xs' || bucket === 'sm';
  const { filters, setFilters, reset } = usePmAnalyticsFilters();

  const [catalog, setCatalog] = useState({ loading: true, error: false, list: [] });
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [recent, setRecent] = useState([]);
  const [family, setFamily] = useState(null);
  const [extra, setExtra] = useState({});
  const [scoreState, setScoreState] = useState('final');
  const [page, setPage] = useState({ pageNumber: 1, pageSize: 20 });
  const [preview, setPreview] = useState({ loading: false, error: false, data: null });
  const [attempt, setAttempt] = useState(0);
  const [exporting, setExporting] = useState(null);
  const [tableHeight, setTableHeight] = useState(420);
  const tableRef = useRef(null);
  const seq = useRef(0);

  const selectedKey = new URLSearchParams(location.search).get(TPL_PARAM);
  const setSelectedKey = useCallback((key) => {
    const p = new URLSearchParams(location.search);
    if (key) p.set(TPL_PARAM, key); else p.delete(TPL_PARAM);
    const s = p.toString();
    history.replace({ pathname: location.pathname, search: s ? `?${s}` : '' });
  }, [history, location.pathname, location.search]);

  const loadCatalog = useCallback(() => {
    setCatalog((c) => ({ ...c, loading: true, error: false }));
    fetchTemplates()
      .then((list) => setCatalog({ loading: false, error: false, list }))
      .catch(() => setCatalog({ loading: false, error: true, list: [] }));
  }, []);

  useEffect(() => {
    loadCatalog();
    fetchPeriods().then((list) => { setPeriods(list); setPeriodsLoaded(true); });
    fetchMyRuns().then(setRecent).catch(() => setRecent([]));
  }, [loadCatalog]);

  const template = useMemo(() => catalog.list.find((tpl) => tpl.key === selectedKey) || null, [catalog.list, selectedKey]);

  // Varsayılan şablon: katalogdaki ilk kayıt (aile sırasıyla).
  useEffect(() => {
    if (catalog.loading || catalog.list.length === 0) return;
    if (!template) {
      const first = [...catalog.list].sort((a, b) => FAMILIES.indexOf(a.family) - FAMILIES.indexOf(b.family))[0];
      setSelectedKey(first.key);
    }
  }, [catalog.loading, catalog.list, template, setSelectedKey]);

  // Dönem zorunlu ve temizlenemez: URL'de yoksa (ya da artık yoksa) en yeni dönem.
  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  // Şablon değişince ek süzgeçler ve puan hali sıfırlanır.
  useEffect(() => { setExtra({}); setScoreState('final'); }, [selectedKey]);

  const query = useMemo(() => buildQuery(template, filters, extra, scoreState), [template, filters, extra, scoreState]);
  const queryKey = JSON.stringify({ key: template?.key, query });
  useEffect(() => { setPage((p) => ({ ...p, pageNumber: 1 })); }, [queryKey]);

  const ready = !!template && (!template.requiresPeriod || !!filters.periodId);
  useEffect(() => {
    if (!ready) return;
    const mine = ++seq.current;
    setPreview((s) => ({ ...s, loading: true, error: false }));
    fetchPreview(template.key, { ...query, pageNumber: page.pageNumber, pageSize: page.pageSize })
      .then((data) => { if (mine === seq.current) setPreview({ loading: false, error: false, data }); })
      .catch(() => { if (mine === seq.current) setPreview({ loading: false, error: true, data: null }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, queryKey, page.pageNumber, page.pageSize, attempt]);

  // Tek kaydırma: tablo gövdesi kalan yüksekliği alır (SurveyDetailPage ölçüm deseni).
  useEffect(() => {
    const el = tableRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => setTableHeight(entry.contentRect.height));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [catalog.loading, catalog.error, catalog.list.length]);

  const data = preview.data;
  const columns = useMemo(() => buildColumns(t, lang, data?.columns), [t, lang, data?.columns]);

  const onTableChanged = useCallback((action, state) => {
    setPage({ pageNumber: (state.page || 0) + 1, pageSize: state.rowsPerPage || 20 });
  }, []);

  const onExport = useCallback(async (format) => {
    if (!template || !data) return;
    setExporting(format);
    await downloadReport(template.key, format, query, buildLabels(t, template, data));
    setExporting(null);
    fetchMyRuns().then(setRecent).catch(() => {});
  }, [template, data, query, t]);

  const familyOptions = useMemo(
    () => FAMILIES.filter((f) => catalog.list.some((tpl) => tpl.family === f))
      .map((f) => ({ id: f, name: t(`performance.reportCenter.family.${f}`) })),
    [catalog.list, t],
  );
  const options = useMemo(() => templateOptions(t, catalog.list, family), [t, catalog.list, family]);
  const total = data?.totalRowCount ?? 0;
  const tooLarge = !!template && total > template.exportRowLimit;
  const header = data?.header;

  if (catalog.loading) return <StateBox data-state="loading">{t('common.loading')}</StateBox>;
  if (catalog.error) {
    return (
      <StateBox data-state="catalog-error">
        <span>{t('performance.reportCenter.loadFailed')}</span>
        <Button variant="outline" onClick={loadCatalog} data-action="retry-catalog">{t('performance.reportCenter.retry')}</Button>
      </StateBox>
    );
  }
  if (catalog.list.length === 0) return <StateBox data-state="no-templates">{t('performance.reportCenter.noTemplates')}</StateBox>;

  const extraFilters = (template?.extraFilters || []).filter((k) => !DATE_FILTERS.includes(k) && !HIDDEN_FILTERS.includes(k));
  const hasDates = (template?.extraFilters || []).some((k) => DATE_FILTERS.includes(k));
  const setExtraValue = (k, v) => setExtra((e) => ({ ...e, [k]: v || undefined }));

  return (
    <>
      <Fixed>
        <Row>
          <Field $w="220px">
            <GenericSelect
              label={t('performance.reportCenter.familyFilter')}
              value={family}
              onChange={setFamily}
              options={familyOptions}
              placeholder={t('performance.reportCenter.allFamilies')}
            />
          </Field>
          <Field $w="420px">
            <GenericSelect
              label={t('performance.reportCenter.template')}
              value={template?.key || null}
              onChange={(v) => v && setSelectedKey(v)}
              options={options}
              clearable={false}
            />
          </Field>
          {recent.length > 0 ? (
            <Meta data-section="recent">
              <span>{t('performance.reportCenter.recentRuns')}</span>
              {recent.filter((r, i) => recent.findIndex((x) => x.templateKey === r.templateKey) === i).map((r) => (
                <Button key={r.id} variant="ghost" onClick={() => setSelectedKey(r.templateKey)} data-recent={r.templateKey}>
                  {t(`performance.reportCenter.templates.${r.templateKey}.title`, { defaultValue: r.templateKey })}
                </Button>
              ))}
            </Meta>
          ) : null}
        </Row>
        {template ? (
          <>
            <Description data-section="description">
              {t(`performance.reportCenter.templates.${template.key}.description`, { defaultValue: '' })}
            </Description>
            <Meta data-section="badges">
              {template.personal ? <Badge $tone="info"><User size={12} />{t('performance.reportCenter.personal')}</Badge> : null}
              {template.hrOnly ? <Badge $tone="warning"><Lock size={12} />{t('performance.reportCenter.hrOnly')}</Badge> : null}
              {template.analysisMetrics.length > 0 ? (
                <span>{t('performance.reportCenter.analysisMetrics', { codes: template.analysisMetrics.join(', ') })}</span>
              ) : null}
            </Meta>
          </>
        ) : null}
        {template?.requiresPeriod && periods.length > 0 ? (
          <PmAnalyticsFilterBar
            periods={periods}
            filters={filters}
            onChange={setFilters}
            onReset={reset}
            lastUpdated={header?.dataSnapshotAt || null}
            stale={!!header?.stale}
            groupMembership={filters.groupIds.length ? 'current' : null}
          />
        ) : null}
        {template?.requiresPeriod && periodsLoaded && periods.length === 0 ? (
          <StateBox data-state="no-periods">{t('performance.reportCenter.noPeriods')}</StateBox>
        ) : null}
        {extraFilters.length > 0 || hasDates || (template?.supportsScoreState && isHr) ? (
          <Row data-section="extra-filters">
            {template?.supportsScoreState && isHr ? (
              <Field>
                <GenericSelect
                  label={t('performance.reportCenter.scoreStateLabel')}
                  value={scoreState}
                  onChange={(v) => setScoreState(v || 'final')}
                  options={['final', 'preliminary'].map((v) => ({ id: v, name: t(`performance.reportCenter.scoreState.${v}`) }))}
                  clearable={false}
                />
              </Field>
            ) : null}
            {extraFilters.map((k) => (
              <Field key={k}>
                <GenericSelect
                  label={t(`performance.reportCenter.extra.${k.replace(/\[.*\]$/, '')}`)}
                  value={extra[k] || null}
                  onChange={(v) => setExtraValue(k, v)}
                  options={k === 'dimension' && template.key === 'experience-survey'
                    ? SURVEY_DIMENSIONS.map((v) => ({ id: v, name: t(`performance.reportCenter.extraValue.dimension.${v}`) }))
                    : extraOptions(t, k, catalog.list, template.key, isHr)}
                  placeholder={t('performance.reportCenter.any')}
                />
              </Field>
            ))}
            {hasDates ? (
              <>
                <Field>
                  <DateTimePicker
                    label={t('performance.reportCenter.dateFrom')}
                    mode="date"
                    value={extra['createDate[gte]'] || ''}
                    onChange={(v, ctx) => { if (!ctx?.validationError) setExtraValue('createDate[gte]', v); }}
                  />
                </Field>
                <Field>
                  <DateTimePicker
                    label={t('performance.reportCenter.dateTo')}
                    mode="date"
                    value={extra['createDate[lte]'] || ''}
                    onChange={(v, ctx) => { if (!ctx?.validationError) setExtraValue('createDate[lte]', v); }}
                  />
                </Field>
              </>
            ) : null}
          </Row>
        ) : null}
        {header ? (
          <Meta data-section="header">
            <Badge>{t(`performance.reportCenter.scope.${header.scope}`)}</Badge>
            {header.scaleName ? <span>{`${t('performance.reportCenter.header.scale')}: ${header.scaleName}`}</span> : null}
            <span>{`${t('performance.reportCenter.header.preparedAt')}: ${formatDateTime(header.preparedAt, lang)}`}</span>
            <span data-meta="threshold">{t('performance.reportCenter.thresholdInfo', { k: header.threshold, n: header.suppressedCells })}</span>
          </Meta>
        ) : null}
        <MetricStrip metrics={data?.metrics} />
        {data && (data.notes.length > 0 || data.uncalculatedMetrics.length > 0) ? (
          <Notes data-section="notes">
            {data.notes.map((n) => <li key={n}>{t(n, { defaultValue: n })}</li>)}
            {data.uncalculatedMetrics.length > 0 ? (
              <li data-note="uncalculated">
                {t('performance.reportCenter.uncalculated', { codes: data.uncalculatedMetrics.map((c) => metricLabel(t, c)).join(', ') })}
              </li>
            ) : null}
          </Notes>
        ) : null}
      </Fixed>
      <TableArea ref={tableRef}>
        {preview.error ? (
          <StateBox data-state="preview-error">
            <span>{t('performance.reportCenter.previewFailed')}</span>
            <Button variant="outline" onClick={() => setAttempt((a) => a + 1)} data-action="retry-preview">{t('performance.reportCenter.retry')}</Button>
          </StateBox>
        ) : (
          <DataTable
            title={template ? t(`performance.reportCenter.templates.${template.key}.title`, { defaultValue: template.key }) : ''}
            columns={columns}
            data={data?.rows || []}
            fetching={preview.loading}
            serverSide
            totalRowCount={total}
            pageIndex={page.pageNumber - 1}
            pageSize={page.pageSize}
            onTableChanged={onTableChanged}
            disableFilters
            canCreateNewRecord={false}
            tableBodyMaxHeight={narrow ? 'none' : `${Math.max(tableHeight - 170, 160)}px`}
            toolbarActions={(
              <>
                <Button
                  variant="outline"
                  onClick={() => onExport('xlsx')}
                  disabled={!data || preview.loading || total === 0 || tooLarge || exporting !== null}
                  loading={exporting === 'xlsx'}
                  title={tooLarge ? t('performance.reportCenter.tooLarge', { limit: template.exportRowLimit }) : undefined}
                  data-action="export-xlsx"
                >
                  <FileSpreadsheet size={14} />
                  {' '}
                  {t('performance.reportCenter.exportXlsx')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onExport('pdf')}
                  disabled={!data || preview.loading || exporting !== null}
                  loading={exporting === 'pdf'}
                  data-action="export-pdf"
                >
                  <FileText size={14} />
                  {' '}
                  {t('performance.reportCenter.exportPdf')}
                </Button>
              </>
            )}
          />
        )}
        {tooLarge ? <Notes><li data-note="too-large">{t('performance.reportCenter.tooLarge', { limit: template.exportRowLimit })}</li></Notes> : null}
        {template && total > template.pdfRowLimit ? (
          <Notes><li data-note="pdf-limit">{t('performance.reportCenter.pdfLimit', { limit: template.pdfRowLimit })}</li></Notes>
        ) : null}
      </TableArea>
    </>
  );
}
